import { Culture, cultureLabels } from "../context/VolunteersHighlightContext/constants";
import {
	KvkkFormData,
	KvkkGeneralFormData,
	KvkkListItem,
	KvkkRules,
	UpdateKvkkLocalizable,
	UpdateKvkkRequest,
} from "../context/KvkkContext/constants";
import { getRichTextPlainText } from "../utils/richText";

const getCultureLabel = (culture: string) => cultureLabels[culture as Culture] ?? culture;

const isTextEmpty = (text = "") => !getRichTextPlainText(text);

const validateTitle = (label: string, title: string) => {
	const trimmed = title.trim();
	return trimmed.length > KvkkRules.titleMaxLength || !KvkkRules.titlePattern.test(trimmed)
		? `${label} başlık en fazla ${KvkkRules.titleMaxLength} karakter olmalı ve geçersiz karakter içermemelidir.`
		: null;
};

const validateGeneral = ({ platform, appId }: { platform?: number; appId?: number }) => {
	if (platform === undefined) {
		return "Lütfen platform seçin.";
	}
	return appId === undefined ? "Lütfen uygulama seçin." : null;
};

// A culture that is created (add form, or a culture the record does not have yet) needs both its title and its text.
const validateCompleteLocalizable = (culture: string, title = "", text = "") => {
	const label = getCultureLabel(culture);

	if (!title.trim()) {
		return `Lütfen ${label} başlık alanını doldurun.`;
	}
	if (isTextEmpty(text)) {
		return `Lütfen ${label} metin alanını doldurun.`;
	}

	return validateTitle(label, title);
};

// An existing culture is patched partially: only the fields that changed are present. The backend keeps the old value
// of a field that is sent empty, so clearing one is refused here instead of being lost silently.
const validateChangedLocalizable = ({ culture, title, text }: UpdateKvkkLocalizable) => {
	const label = getCultureLabel(culture);

	if (title !== undefined) {
		const titleError = title.trim() ? validateTitle(label, title) : `${label} başlık boş bırakılamaz.`;
		if (titleError) {
			return titleError;
		}
	}
	if (text !== undefined && isTextEmpty(text)) {
		return `${label} metin boş bırakılamaz.`;
	}

	return null;
};

const isSameCombination = (item: KvkkListItem, data: KvkkGeneralFormData) =>
	item.platform === data.platform && item.appId === data.appId && item.isPage === data.isPage && item.isManager === data.isManager;

export class KvkkValidator {
	// The Turkish texts are required; the other culture is either filled in completely or left empty.
	static readonly validateAddKvkk = (data: KvkkFormData): string | null => {
		const generalError = validateGeneral(data);
		if (generalError) {
			return generalError;
		}

		for (const localizable of data.kvkkTextLocalizable) {
			const isFilled = Boolean(localizable.title.trim()) || !isTextEmpty(localizable.text);
			if (isFilled || localizable.culture === KvkkRules.requiredCulture) {
				const error = validateCompleteLocalizable(localizable.culture, localizable.title, localizable.text);
				if (error) {
					return error;
				}
			}
		}

		return null;
	};

	// The request only carries changed fields, so an empty value here means the user cleared a filled field.
	static readonly validateUpdateKvkk = (data: UpdateKvkkRequest): string | null => {
		for (const localizable of data.kvkkTextLocalizable ?? []) {
			const error = localizable.kvkkTextLocalizableId
				? validateChangedLocalizable(localizable)
				: validateCompleteLocalizable(localizable.culture, localizable.title, localizable.text);
			if (error) {
				return error;
			}
		}

		return null;
	};

	// The site and the applications read one text per platform, application and kind; with two of them the backend would
	// show either, so a second record of the same kind is refused. exceptId is the record being updated.
	static readonly validateUniqueKvkk = (items: KvkkListItem[], data: KvkkGeneralFormData, exceptId?: string): string | null =>
		items.some(item => item.kvkkTextId !== exceptId && isSameCombination(item, data))
			? "Bu platform ve uygulama için aynı türde bir KVKK metni zaten var. Lütfen mevcut metni güncelleyin."
			: null;
}
